import { Box, ThemeProvider } from '@mui/material'
import UploadModal from '../Upload/UploadModal'
import Header from '../../views/Layout/Header'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import ProgressModal from '../ProgressModal'
import theme from '../../styles/theme'
import Building from '../../views/Building'
import { useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  setFileName,
  setIs3DFetchBuildingInfo,
  setIs3DViewActive,
  setShowProgressModal,
  setShowUploadModal,
} from '../../actions/construction'
import { fetchReviewStatus } from '../../services/reviewService'
import { updateRecentFileAsync } from '../../services/filesService'
import { createSelector } from '@reduxjs/toolkit'
import {
  updateSelectedFile,
  updateSelectedProject,
} from '../../actions/organization'
import { architectureRoles, engineeringRoles } from '../../models/AccessControl'
import { setSelectedProject } from '../../actions/review'
import { fetchBuildingDocument } from '../../services/buildingService'
import { BuildingDocumentType } from '../../models/Construction'

const userSelector = createSelector(
  [(state: RootState) => state.auth],
  (auth) => auth.user,
)
const filesSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.files,
)
const projectsSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.projects,
)
const selectedProjectSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.selectedProject,
)
const isShowUploadModalSelector = createSelector(
  [(state: RootState) => state.construction],
  (construction) => construction.showUploadModal,
)
const assignedProjectsSelector = createSelector(
  [(state: RootState) => state.review],
  (review) => review.assignedProjects,
)

function FileComponent() {
  const location = useLocation()
  const dispatch = useDispatch<any>()
  const user = useSelector(userSelector)
  const projects = useSelector(projectsSelector)
  const files = useSelector(filesSelector)
  const selectedProject = useSelector(selectedProjectSelector)
  const isShowUploadModal = useSelector(isShowUploadModalSelector)
  const assignedProjects = useSelector(assignedProjectsSelector)

  const { projectId, fileId } = useParams<{
    projectId: string
    fileId: string
  }>()
  const navigate = useNavigate()

  useEffect(() => {
    if (user) {
      if (architectureRoles.includes(user.role.name)) {
        const is3DViewActive =
          !!projectId && !!fileId && !!Number.parseFloat(fileId)
        dispatch(setIs3DViewActive(is3DViewActive))
        if (is3DViewActive) {
          const parsedId = Number.parseFloat(fileId)
          const fileInfo = files.find((file) => file.id === parsedId)
          if (fileInfo) {
            const parsedProjectId = Number.parseFloat(projectId) as any
            const project = projects.find(
              (project) => project.id === parsedProjectId,
            )
            if (!isShowUploadModal) {
              dispatch(setShowProgressModal(true))
              dispatch(setIs3DFetchBuildingInfo(true))
              dispatch(
                fetchReviewStatus({ file_id: fileInfo.id, organization_id: 9 }),
              )
              if (fileInfo.path) {
                dispatch(
                  fetchBuildingDocument({
                    filePath: fileInfo.path,
                    doc_type: BuildingDocumentType.Report,
                  }),
                )
                dispatch(
                  fetchBuildingDocument({
                    filePath: fileInfo.path,
                    doc_type: BuildingDocumentType.Structural,
                  }),
                )
                dispatch(
                  fetchBuildingDocument({
                    filePath: fileInfo.path,
                    doc_type: BuildingDocumentType.Floorplans2D,
                  }),
                )
                dispatch(setFileName(fileInfo.path))
              }
            }
            dispatch(setShowUploadModal(false))
            dispatch(setIs3DViewActive(true))
            dispatch(updateSelectedProject(project))
            dispatch(updateSelectedFile(fileInfo))
            dispatch(
              updateRecentFileAsync({ fileId: fileInfo.id, userId: user?.id! }),
            )
          } else {
            navigate(-1)
          }
        } else {
          if (!selectedProject) {
            navigate('/projects')
          } else if (location.pathname.includes('uploadFiles')) {
            const parsedId =
              !!projectId && (Number.parseFloat(projectId) as any)
            const project = projects.find((project) => project.id === parsedId)
            if (!project) {
              navigate('/projects')
            }

            dispatch(setShowUploadModal(true))
            dispatch(setIs3DViewActive(false))
          }
        }
      }

      if (engineeringRoles.includes(user.role.name)) {
        if (!!fileId && !!Number.parseFloat(fileId)) {
          const parsedId = Number.parseFloat(fileId)
          const assignedProject = assignedProjects.find(
            (project) => project.file_id === parsedId,
          )
          if (assignedProject) {
            dispatch(setShowProgressModal(true))
            dispatch(setSelectedProject(assignedProject.id))
            dispatch(setShowUploadModal(false))
            dispatch(setIs3DViewActive(true))
            dispatch(setIs3DFetchBuildingInfo(true))
            dispatch(
              fetchBuildingDocument({
                filePath: assignedProject.file.path,
                doc_type: BuildingDocumentType.Report,
              }),
            )
            dispatch(
              fetchBuildingDocument({
                filePath: assignedProject.file.path,
                doc_type: BuildingDocumentType.Structural,
              }),
            )
            dispatch(
              fetchBuildingDocument({
                filePath: assignedProject.file.path,
                doc_type: BuildingDocumentType.Floorplans2D,
              }),
            )
          } else {
            navigate('/projects')
          }
        } else {
          navigate('/projects')
        }
      }
    }
  }, [projectId, fileId, location])

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          minWidth: '800px',
        }}
      >
        <Header />
        {isShowUploadModal ? <UploadModal /> : <Building />}
        <ProgressModal text='Opening file'></ProgressModal>
      </Box>
    </ThemeProvider>
  )
}

export default FileComponent
